/**
 * Textes et données de contenu, repris mot pour mot de la maquette
 * (reference/site-riseafrica-v10.html). Elle est la référence de vérité pour la
 * copie : ne pas réécrire ces chaînes.
 */
import { GROUPS, MASTERY_AXES } from "@riseafrica/scoring";
import type { GroupCode } from "@riseafrica/scoring";

/**
 * Métiers proposés sous le champ de saisie.
 *
 * Trois critères, dans cet ordre.
 *
 * **Le contexte.** La liste d'origine tenait en huit postes de bureau. Elle
 * parlait à un cadre d'Abidjan et à personne d'autre — or le produit s'adresse
 * aussi à qui tient une boutique, coud, conduit, soigne ou enseigne. Un visiteur
 * qui ne se reconnaît dans aucune suggestion conclut que le site n'est pas pour
 * lui, avant même d'avoir essayé le champ libre qui, lui, accepte tout.
 *
 * **Le coût.** Chaque suggestion est un métier que quelqu'un va demander. Si le
 * cache est froid, ce visiteur paie une génération complète et attend trente
 * secondes — pour avoir cliqué sur ce que nous lui avons proposé. Cette liste
 * est donc **réchauffée d'avance** par `npm run prechauffer` : proposer un
 * métier et le faire attendre serait notre faute, pas la sienne.
 *
 * **La vérifiabilité.** Que des métiers réels, exercés ici, nommés comme ils se
 * nomment sur place — « transitaire » à Abidjan, « gérant de boutique », pas des
 * intitulés de fiche de poste européenne.
 */
export const SUGGESTS = [
  // Bureau et gestion
  "Comptable",
  "Assistante de direction",
  "Secrétaire",
  "Gestionnaire de paie",
  "Chargé de clientèle",
  "Agent de microfinance",
  "Agent d'assurance",
  // Commerce et informel structuré
  "Commerçant",
  "Gérant de boutique",
  "Caissière",
  "Vendeur",
  // Métiers manuels et techniques
  "Couturier",
  "Coiffeuse",
  "Mécanicien",
  "Électricien",
  "Menuisier",
  "Chef de chantier",
  "Technicien de maintenance",
  // Transport et logistique — Abidjan est un port
  "Chauffeur",
  "Transitaire",
  "Agent de sécurité",
  // Santé, éducation, public
  "Infirmier",
  "Sage-femme",
  "Instituteur",
  "Enseignant",
  "Secrétaire médicale",
  // Numérique et communication
  "Développeur",
  "Community manager",
  "Journaliste",
  "Agent immobilier",
] as const;

/**
 * Les suggestions affichées sous le champ, sur mobile.
 *
 * Trente boutons feraient quinze lignes sur un téléphone et repousseraient le
 * reste de la page hors de l'écran. Celles-ci couvrent les familles — bureau,
 * commerce, manuel, transport, santé, éducation, numérique — de sorte que
 * chacun voie au moins un métier qui ressemble au sien, et comprenne que le
 * champ accepte le reste. La liste entière, elle, alimente l'autocomplétion.
 */
export const SUGGESTS_VISIBLES = [
  "Comptable",
  "Commerçant",
  "Couturier",
  "Chauffeur",
  "Infirmier",
  "Enseignant",
  "Community manager",
  "Chef de chantier",
] as const;

/**
 * Les suggestions de celui qui travaille à son compte — 25/08/2026.
 *
 * Elles ne sont pas cosmétiques. Les exemples sont le signal le plus fort de
 * « pour qui c'est », plus fort que n'importe quel titre : cinq métiers de
 * bureau salariés disaient à un indépendant qu'il s'était trompé de porte,
 * quoi qu'on écrive au-dessus.
 *
 * Le choix des cinq suit la même règle que la liste salariée — couvrir des
 * familles plutôt que des variantes d'un même monde — avec un critère de plus :
 * ce sont des activités **vendues à des clients**, pas occupées dans une
 * entreprise. Et elles sont franchement exposées : quelqu'un qui se reconnaît
 * ici a une vraie raison de continuer.
 */
export const SUGGESTS_INDEPENDANTS = [
  "Graphiste",
  "Consultant",
  "Couturière",
  "Photographe",
  "Traducteur",
  "Coach",
  "Développeur",
  "Rédacteur",
] as const;

/**
 * Le message qu'un dirigeant envoie à son équipe — écrit de sa main, au
 * tutoiement d'un patron à ses salariés. Il vit ici parce qu'il sert à deux
 * endroits : l'écran de résultat dirigeant, et la fin du paiement à 15 000 F,
 * où il manquait — le parrainage est l'indicateur n° 1 de cet axe, et rien ne
 * le proposait au moment où le dirigeant venait de payer. Relecture du 06/09/2026.
 */
export const MESSAGE_EQUIPE =
  "Je viens de faire un diagnostic sur les tâches que l'IA fait déjà à notre place. " +
  "Fais le tien, ça prend moins de deux minutes : c'est gratuit, sans inscription, et je ne vois pas " +
  "ton résultat — il n'est enregistré au nom de personne. Ça nous servira à en parler.";

/** Exemple de CV, pour essayer le mode CV sans coller son propre document. */
export const DEMO_CV =
  "Vétérinaire en clinique privée, 8 ans d'expérience. Consultations et diagnostics sur animaux de compagnie. Chirurgies courantes. Rédaction des comptes rendus de consultation et des ordonnances. Gestion des rappels de vaccination par téléphone et WhatsApp. Tenue du registre des dossiers patients et facturation. Commande et suivi des stocks de médicaments. Formation de deux assistants.";

/** Les huit actions du rapport payant, affichées floutées. */
/*
  `MASKED` a été retiré. C'étaient les huit intitulés de rubrique génériques des
  cartes floutées — « la compétence à acquérir en priorité » —, identiques pour
  tout le monde. Les neuf actions verrouillées portent désormais leur vrai titre,
  produit pour le métier de la personne : il n'y a plus rien à masquer, il y a
  quelque chose à montrer.
*/

/** Première action concrète, dérivée du groupe professionnel. */
export const FIRST_STEP: Record<GroupCode, readonly [string, string]> = {
  ADMIN: [
    "Faites dicter vos comptes rendus, pas taper",
    "Enregistrez votre prochaine réunion, faites-en produire le compte rendu structuré, puis relisez-le. Vous passerez de quarante minutes à cinq — et c'est visible immédiatement par ceux pour qui vous travaillez.",
  ],
  COMPTA: [
    "Traitez vos dix prochaines pièces par photo",
    "Photographiez dix factures, faites-en extraire les données, contrôlez ligne par ligne. Vous mesurerez tout de suite ce qui est fiable et ce qui exige encore votre œil.",
  ],
  // CREA, IT, SANTE, DIRECTION réécrits le 05/10/2026 avec COMMERCIAL : même
  // défaut, un texte écrit pour un seul métier du groupe. « Facturez » ne
  // parlait qu'à l'indépendant ; « le code » qu'au développeur ; « patients »
  // et « gestes » ignoraient le social ; et « votre poste est peu exposé »
  // pouvait contredire l'indice affiché juste au-dessus.
  CREA: [
    "Déléguez la production, gardez l'angle",
    "Sur votre prochain contenu, notez le temps passé à produire et celui passé à choisir l'angle. Le premier va fondre ; c'est sur le second que votre valeur se joue.",
  ],
  // Réécrit le 05/10/2026. « Synthèse de dossier, simulation, clients reçus » :
  // c'était le quotidien d'un conseiller en agence, servi tel quel à un
  // directeur commercial ou à un vendeur — tout le groupe lit ce texte.
  COMMERCIAL: [
    "Faites préparer vos rendez-vous, gardez la négociation",
    "Avant vos trois prochains rendez-vous clients, faites préparer la fiche du client, la proposition et le message de relance. Vous gardez l'échange et le prix ; le temps gagné passe en clients vus.",
  ],
  RH: [
    "Prenez l'évaluation, lâchez la présélection",
    "Sur votre prochain recrutement, laissez le tri initial à l'outil et consacrez ce temps aux entretiens et à l'argumentaire de décision. C'est ce qui vous reste.",
  ],
  JURIDIQUE: [
    "Faites résumer, gardez la signature",
    "Sur votre prochain contrat, faites produire un résumé des clauses à risque, puis vérifiez-le vous-même. Votre valeur est dans l'engagement, pas dans la lecture.",
  ],
  IT: [
    "Faites produire le premier jet, gardez la vérification",
    "Sur votre prochaine tâche — code, script, requête, procédure ou documentation —, faites produire une première version et concentrez-vous sur la vérification, la sécurité et la mise en service. C'est la compétence qui reste rare.",
  ],
  SANTE: [
    "Reprenez le temps volé par l'écrit",
    "Votre présence auprès des personnes n'est pas menacée, votre paperasse l'est. Faites produire vos comptes rendus et vos courriers : c'est du temps rendu à ceux que vous soignez ou accompagnez.",
  ],
  TERRAIN: [
    "Faites écrire vos papiers, gardez le geste",
    "Votre exposition est faible, mais vos reçus, vos messages aux clients, vos devis et vos comptes rendus peuvent être écrits en quelques minutes par un assistant, entre deux interventions.",
  ],
  ENSEIGNEMENT: [
    "Faites préparer vos supports, gardez la classe",
    "Exercices, corrigés, différenciation : c'est là que vous récupérez des heures sans rien perdre de votre valeur en classe.",
  ],
  DIRECTION: [
    "Regardez l'exposition de votre équipe avant la vôtre",
    "Les tâches de vos collaborateurs sont souvent plus exposées que les vôtres. Le risque que vous portez se mesure à l'échelle de votre équipe, pas seulement de votre poste.",
  ],
};

/**
 * Bandeau d'activité. Données d'exemple, signalées comme telles et affichées
 * uniquement quand `demoData` est actif. À remplacer par les derniers
 * diagnostics anonymes (ticket T14).
 *
 * Reformulé le 27/08/2026 : la version d'origine, reprise mot pour mot de la
 * maquette, annonçait un métier précis, une ville et un score exact
 * (« Comptable, Abidjan · 74/100 »). Deux règles nées après la maquette
 * l'interdisent l'une comme l'autre. `Ticker.tsx`, qui affiche ce tableau,
 * documente lui-même que le bandeau « n'affiche jamais un métier précis avec
 * un score exact — dans un groupe peu fourni, ce couple suffirait à
 * reconnaître quelqu'un » ; et l'arbitrage du 25/08/2026 interdit tout nom de
 * ville ou de pays dans ce qui s'adresse au visiteur. Un exemple qui viole
 * les deux règles qu'il est censé illustrer, même signalé comme exemple,
 * apprend la mauvaise habitude à quiconque le lit.
 *
 * La forme suit donc exactement celle du flux réel (`GROUPS[groupe].label —
 * indice tranche`, sans horodatage ni lieu — le vrai flux n'en porte pas non
 * plus) plutôt que d'inventer une mise en forme propre à la démonstration.
 */
export const DEMO_FEED = [
  `${GROUPS.COMPTA.label} — indice 70-79`,
  `${GROUPS.ADMIN.label} — indice 60-69`,
  `${GROUPS.COMMERCIAL.label} — indice 50-59`,
  `${GROUPS.SANTE.label} — indice 20-29`,
  `${GROUPS.IT.label} — indice 70-79`,
  `${GROUPS.JURIDIQUE.label} — indice 50-59`,
  `${GROUPS.CREA.label} — indice 40-49`,
] as const;

/** Volet 3 — huit mises en situation, trois options notées 0, 1 ou 2. */
export interface QuizQuestion {
  q: string;
  o: ReadonlyArray<readonly [string, number]>;
}

/**
 * Le questionnaire du volet 3, dérivé des axes du barème.
 *
 * Il était recopié à la main à côté de `MASTERY_AXES`, ce qui laissait deux
 * listes se répondre par bonne volonté. Elles sont maintenant la même : les
 * deux questions de chaque axe sont produites ici dans l'ordre où le barème les
 * attend — question tailleur puis question témoin, axe après axe.
 *
 * Cet ordre n'est pas cosmétique : `computeMastery` apparie les réponses deux à
 * deux pour repérer les contradictions. Les intervertir ferait comparer des axes
 * différents entre eux.
 */
export const QUIZ: ReadonlyArray<QuizQuestion> = MASTERY_AXES.flatMap((axe) => [
  { q: axe.q, o: axe.o.map((texte: string, points: number) => [texte, points] as const) },
  { q: axe.q2, o: axe.o2.map((texte: string, points: number) => [texte, points] as const) },
]);


/*
  Les moyens de paiement ne sont plus une liste unique — 11/10/2026.
  Ils dépendent du pays : voir `lib/pays.ts`, relevés sur la caisse Chariow.
*/

/** Étapes affichées pendant l'analyse. */
export const SCAN_LINES_METIER = [
  "Reconnaissance du métier…",
  "Établissement des tâches types…",
  "Classement par niveau d'exposition…",
] as const;

export const SCAN_LINES_CV = [
  "Lecture du texte…",
  "Identification des tâches…",
  "Classement par niveau d'exposition…",
  "Calcul de l'indice…",
] as const;
